// src/hooks/usePullToRefresh.js
import { useEffect, useRef, useState } from "react";

/**
 * Pull-to-refresh for a scroll container (touch-only).
 * Triggers when user pulls down from scrollTop === 0 past `threshold` px.
 */
export function usePullToRefresh({
  container,
  onRefresh,
  threshold = 72,
  maxPull = 140,
}) {
  const [offset, setOffset] = useState(0); // visual pull distance
  const [isRefreshing, setIsRefreshing] = useState(false);
  const pullingRef = useRef(false);
  const startYRef = useRef(0);
  const rafRef = useRef();

  useEffect(() => {
    if (!container) return;
    const el = container;

    const onTouchStart = (e) => {
      if (isRefreshing) return;
      if (el.scrollTop > 0) return; // only when at the very top
      pullingRef.current = true;
      startYRef.current = e.touches?.[0]?.clientY ?? 0;
    };

    const onTouchMove = (e) => {
      if (!pullingRef.current) return;
      const currentY = e.touches?.[0]?.clientY ?? 0;
      const dy = currentY - startYRef.current;
      if (dy <= 0) {
        setOffset(0);
        return;
      }
      // resistance
      const resisted = Math.min(maxPull, dy * 0.5);
      // Prevent native page bounce while pulling
      if (resisted > 0) e.preventDefault();
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => setOffset(resisted));
    };

    const onTouchEnd = async () => {
      if (!pullingRef.current) return;
      pullingRef.current = false;

      if (offset >= threshold && !isRefreshing) {
        try {
          setIsRefreshing(true);
          await onRefresh?.();
        } finally {
          // collapse after refresh
          setOffset(0);
          setIsRefreshing(false);
        }
      } else {
        // snap back
        setOffset(0);
      }
    };

    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    el.addEventListener("touchcancel", onTouchEnd, { passive: true });

    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
      cancelAnimationFrame(rafRef.current);
    };
  }, [container, onRefresh, isRefreshing, offset, threshold, maxPull]);

  return { offset, isRefreshing };
}
